import React, { useMemo } from 'react';
import { AnalysisModel, MissingData, PlayerModel, PuttingRow, RoundFilter, puttingStats } from '../../utils/majorAnalysis';
import { PUTT_COLORS } from '../../utils/chartPalette';
import { ParsAsk, PuttsPenaltiesAsk } from './DataNeeded';
import { Empty, PlayerCell, RankCell, SortTh, fmt1, fmtPct, sortRows, useSort } from './shared';

interface Props {
  model: AnalysisModel;
  players: PlayerModel[];
  filter: RoundFilter;
  missing: MissingData;
  onOpenRound: (roundId: number) => void;
}

const PUTT_LABEL = ['0 putts (holed out)', '1 putt', '2 putts', '3+ putts'];

const PuttBar: React.FC<{ dist: PuttingRow['dist']; holes: number; label: string }> = ({ dist, holes, label }) => (
  <div className="ma-mix" role="img" aria-label={`${label}: ${dist.map((n, i) => `${n} × ${PUTT_LABEL[i]}`).join(', ')}`}>
    {dist.map((n, i) => (n > 0
      ? <span key={i} style={{ width: `${(n / holes) * 100}%`, background: PUTT_COLORS[i] }} title={`${PUTT_LABEL[i]}: ${n} (${fmtPct(n, holes)})`} />
      : null))}
  </div>
);

const Putting: React.FC<Props> = ({ model, players, filter, missing, onOpenRound }) => {
  const rows = useMemo(() => puttingStats(model, filter), [model, filter]);
  const { sort, toggle } = useSort();
  const byId = new Map(rows.map((r) => [r.player_id, r]));
  const holes = rows.reduce((n, r) => n + r.holes, 0);

  if (holes === 0) {
    return (
      <div className="ma-tab">
        <Empty title="No putts entered for this major yet.">
          <p className="ma-lede">Putting stats need putts per hole (and the hole scores they go with) for each round.</p>
          <PuttsPenaltiesAsk putts={missing.putts} penalties={[]} onOpenRound={onOpenRound} show="putts" />
        </Empty>
      </div>
    );
  }

  const val = (r: PuttingRow | undefined, key: string): number | null => {
    if (!r || !r.holes) return null;
    switch (key) {
      case 'holes': return r.holes;
      case 'putts': return r.putts;
      case 'per18': return r.per18;
      case 'one': return r.dist[1] / r.holes;
      case 'three': return r.dist[3] / r.holes;
      case 'gir': return r.gir.n ? r.gir.hit / r.gir.n : null;
      case 'pgir': return r.gir.hit ? r.puttsOnGir / r.gir.hit : null;
      case 'scr': return r.scrambling.chances ? r.scrambling.made / r.scrambling.chances : null;
      default: return null;
    }
  };
  const sorted = sortRows(players, sort, (p) => p.order, (p, key) => val(byId.get(p.player_id), key));
  const girMissing = rows.some((r) => r.holes > 0 && r.gir.n < r.holes);

  return (
    <div className="ma-tab">
      <p className="ma-lede">
        From the putts entered per hole ({holes} holes). A green in regulation (GIR) = on the green with two strokes to spare
        for par (strokes − putts ≤ par − 2). Scrambling = par or better after missing the green.
      </p>
      <div className="ma-legend ma-legend--static" aria-hidden>
        {PUTT_LABEL.map((l, i) => <span key={l} className="ma-legend-item"><span className="ma-swatch ma-swatch--sq" style={{ background: PUTT_COLORS[i] }} />{l}</span>)}
      </div>
      <div className="ma-scroll">
        <table className="tbl tbl--sm ma-table ma-table--sticky">
          <thead>
            <tr>
              <SortTh label="Rank" sortKey="rank" sort={sort} onSort={toggle} className="c ma-stick-1" />
              <th className="ma-stick-2">Player</th>
              <SortTh label="Holes" sortKey="holes" sort={sort} onSort={toggle} firstDir={-1} className="c" title="Holes with putts entered" />
              <SortTh label="Putts" sortKey="putts" sort={sort} onSort={toggle} className="c" />
              <SortTh label="Per 18" sortKey="per18" sort={sort} onSort={toggle} className="c" />
              <th className="ma-mix-col">Putts per hole</th>
              <SortTh label="1-putts" sortKey="one" sort={sort} onSort={toggle} firstDir={-1} className="c" />
              <SortTh label="3-putts+" sortKey="three" sort={sort} onSort={toggle} className="c" />
              <SortTh label="GIR" sortKey="gir" sort={sort} onSort={toggle} firstDir={-1} className="c" title="Greens in regulation" />
              <SortTh label="Putts / GIR" sortKey="pgir" sort={sort} onSort={toggle} className="c" />
              <SortTh label="Scrambling" sortKey="scr" sort={sort} onSort={toggle} firstDir={-1} className="c" />
            </tr>
          </thead>
          <tbody>
            {sorted.map((p) => {
              const r = byId.get(p.player_id);
              const has = !!r && r.holes > 0;
              return (
                <tr key={p.player_id}>
                  <RankCell p={p} all={model.players} />
                  <PlayerCell p={p} />
                  <td className="c">{has ? r!.holes : '—'}</td>
                  <td className="c">{has ? r!.putts : '—'}</td>
                  <td className="c ma-strong">{has ? fmt1(r!.per18) : '—'}</td>
                  <td className="ma-mix-col">{has && <PuttBar dist={r!.dist} holes={r!.holes} label={p.name} />}</td>
                  <td className="c">{has ? `${r!.dist[1]} (${fmtPct(r!.dist[1], r!.holes)})` : '—'}</td>
                  <td className="c">{has ? `${r!.dist[3]} (${fmtPct(r!.dist[3], r!.holes)})` : '—'}</td>
                  <td className="c">{has && r!.gir.n ? `${fmtPct(r!.gir.hit, r!.gir.n)}` : '—'}</td>
                  <td className="c">{has && r!.gir.hit ? fmt1(r!.puttsOnGir / r!.gir.hit) : '—'}</td>
                  <td className="c">{has && r!.scrambling.chances ? fmtPct(r!.scrambling.made, r!.scrambling.chances) : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {missing.putts.length > 0 && (
        <>
          <p className="ma-foot">Based on {holes} holes — putts are still missing for some rounds:</p>
          <PuttsPenaltiesAsk putts={missing.putts} penalties={[]} onOpenRound={onOpenRound} show="putts" />
        </>
      )}
      {girMissing && (
        <>
          <p className="ma-foot">GIR and scrambling leave out holes with no par.</p>
          <ParsAsk missing={missing} />
        </>
      )}
    </div>
  );
};

export default Putting;
